import { cn } from '@neo/lib/utils'
import { ReactFlowProvider } from '@xyflow/react'
import { useEffect } from 'react'
import '@xyflow/react/dist/style.css'
import './style.scss'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { useParams } from 'router-v6'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import ExternalApps from '../settings/integrations/apps/external-apps'
import { AgentBuilderLoading } from './components/agent-builder-loading'
import { AgentNotFound } from './components/agent-not-found'
import { IntegrationsMigrationBanner } from './components/integrations-migration-banner'
import { TitleBar } from './components/title-bar'
import { LegacyReadOnlyBanner } from './components/title-bar/legacy-readonly-banner'
import { VibeAgentSidebar } from './components/vibe-agent/components/sidebar'
import { useAgentSettingsVisibility } from './hooks/use-agent-settings-visibility'
import { useCopyPaste } from './hooks/use-copy-paste'
import { useFlowId } from './hooks/use-flow-id'
import { useHasAINode } from './hooks/use-has-ai-node'
import { FlowCanvasContent } from './index'
import { useFlowQuery } from './queries'
import { useFlowStore } from './store'
import { AgentFlowRunsTab } from './tabs/agent-flow-runs'
import { AgentKnowledgeBaseTab } from './tabs/agent-knowledge-base'
import { AgentSecretsTab } from './tabs/agent-secrets-tab'
import { AgentSettingsTab } from './tabs/agent-settings-tab'
import { EventCallbacksTab } from './tabs/event-callbacks-tab'
import { MetricsTab } from './tabs/metrics-tab'
import { SimulationTab } from './tabs/simulation-tab'
import { VoiceConfigTab } from './tabs/voice-config-tab'
import { isLegacyReadOnly } from './utils'

// Shared frame for every tab panel: fills the column, hides when inactive, and
// keeps every builder section on the same bordered surface. Extend per-tab with cn(...).
const TAB_CONTENT_CLASS =
  'mx-3 mb-3 flex-1 min-h-0 rounded-lg border border-border bg-background data-[state=inactive]:hidden'

const TAB_TRIGGER_CLASS =
  'mx-0 -mb-px h-11 rounded-t-md border-b-2 px-4 py-0 text-s-500 text-secondary hover:bg-hv1 hover:text-foreground focus-visible:ring-brand data-[state=active]:border-brand data-[state=active]:text-foreground'

type AgentBuilderTab =
  | 'canvas'
  | 'metrics'
  | 'knowledge-base'
  | 'runs'
  | 'settings'
  | 'secrets'
  | 'external-tools'
  | 'voice-config'
  | 'event-callbacks'
  | 'simulation'

const AgentBuilderContent = () => {
  const { id } = useParams()
  const navigate = useAppNavigate()
  const { isNew } = useFlowId()
  const { data: flowData, ...queryData } = useFlowQuery(id || '', undefined, isNew)
  const { pasteContent } = useCopyPaste()
  const { showSettingsTab, showMetricsTab, showVoiceConfigTab, showEventCallbacksTab } =
    useAgentSettingsVisibility()
  // Knowledge Base, Secrets, Tools and Simulations are not on hodor's embed allow-list yet,
  // so they are hidden for every embed session.
  const isStageOneEmbed = isEmbedMode()
  const showAgentResourceTabs = useHasAINode() && !isStageOneEmbed
  const { isSimulationEvalEnabled, vibeAgentDisabled } = useFeatureFlags()
  const { isLivekitEnabled } = useLivekitEnabled()
  // Simulations run through the vibe/AI agent, so hide the tab when the vibe
  // agent is disabled (SER-6067) — not just behind its own eval flag.
  const showSimulationTab =
    isSimulationEvalEnabled && isLivekitEnabled && !vibeAgentDisabled && !isStageOneEmbed

  const getCurrentTab = (): AgentBuilderTab => {
    if (location.pathname.includes('/metrics') && showMetricsTab) return 'metrics'
    if (location.pathname.includes('/knowledge-base') && showAgentResourceTabs)
      return 'knowledge-base'
    if (location.pathname.includes('/runs')) return 'runs'
    if (location.pathname.includes('/simulation') && showSimulationTab) return 'simulation'
    if (location.pathname.includes('/settings') && showSettingsTab) return 'settings'
    if (location.pathname.includes('/voice-config') && showVoiceConfigTab) return 'voice-config'
    if (location.pathname.includes('/event-callbacks') && showEventCallbacksTab)
      return 'event-callbacks'
    if (location.pathname.includes('/secrets') && showAgentResourceTabs) return 'secrets'
    if (location.pathname.includes('/external-tools') && showAgentResourceTabs)
      return 'external-tools'
    return 'canvas'
  }

  const currentTab = getCurrentTab()
  const requestedPath = useFlowStore((s) => s.requestedPath)
  const basePath = `/agent/builder/${id}`

  const handleTabSwitch = (tab: string) => {
    const pathMap: Record<AgentBuilderTab, string> = {
      canvas: '',
      metrics: '/metrics',
      'knowledge-base': '/knowledge-base',
      runs: '/runs',
      simulation: '/simulation',
      settings: '/settings',
      'voice-config': '/voice-config',
      'event-callbacks': '/event-callbacks',
      secrets: '/secrets',
      'external-tools': '/external-tools',
    }
    navigate(`${basePath}${pathMap[tab as AgentBuilderTab]}${location.search}`, { replace: true })
  }

  useEffect(() => {
    if (requestedPath) {
      navigate(`${basePath}${requestedPath}`, { replace: true })
      useFlowStore.getState().setRequestedPath(null)
    }
  }, [requestedPath])

  if (queryData.isPending) return <AgentBuilderLoading />
  if (flowData === null) return <AgentNotFound />

  return (
    <div
      className="flex h-full flex-col bg-bg2 font-inter text-foreground"
      data-testid="agent-builder"
    >
      <TitleBar flowData={flowData} pasteContent={pasteContent} />

      {flowData && isLegacyReadOnly(flowData.phlo) && <LegacyReadOnlyBanner flowData={flowData} />}

      <Tabs
        value={currentTab}
        onValueChange={handleTabSwitch}
        className="flex flex-col flex-1 min-h-0 border-t-0"
      >
        <TabsList
          variant="simple"
          aria-label="Agent builder sections"
          className="relative z-10 min-h-12 w-full flex-shrink-0 gap-0 overflow-x-auto border-b border-border bg-background px-3 pl-6"
        >
          <TabsTrigger
            value="canvas"
            variant="simple"
            className={TAB_TRIGGER_CLASS}
            data-testid="tab-canvas"
          >
            Flow
          </TabsTrigger>
          {showMetricsTab && (
            <TabsTrigger
              value="metrics"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-metrics"
            >
              Metrics
            </TabsTrigger>
          )}
          <TabsTrigger
            value="runs"
            variant="simple"
            className={TAB_TRIGGER_CLASS}
            data-testid="tab-runs"
          >
            Agent Runs
          </TabsTrigger>
          {showSimulationTab && (
            <TabsTrigger
              value="simulation"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-simulation"
            >
              Simulations
            </TabsTrigger>
          )}
          {showEventCallbacksTab && (
            <TabsTrigger
              value="event-callbacks"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-event-callbacks"
            >
              Event Callbacks
            </TabsTrigger>
          )}
          {showSettingsTab && (
            <TabsTrigger
              value="settings"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-settings"
            >
              Settings
            </TabsTrigger>
          )}
          {showAgentResourceTabs && (
            <TabsTrigger
              value="knowledge-base"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-knowledge-base"
            >
              Knowledge Base
            </TabsTrigger>
          )}
          {showAgentResourceTabs && (
            <TabsTrigger
              value="secrets"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-secrets"
            >
              Secrets
            </TabsTrigger>
          )}
          {showAgentResourceTabs && (
            <TabsTrigger
              value="external-tools"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-external-tools"
            >
              Tools
            </TabsTrigger>
          )}
          {showVoiceConfigTab && (
            <TabsTrigger
              value="voice-config"
              variant="simple"
              className={TAB_TRIGGER_CLASS}
              data-testid="tab-voice-config"
            >
              Voice Configuration
            </TabsTrigger>
          )}
        </TabsList>

        <div className="flex flex-1 min-h-0 relative">
          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            <TabsContent value="canvas" className={TAB_CONTENT_CLASS} forceMount>
              <FlowCanvasContent />
            </TabsContent>

            {/* Same flag as the trigger and the route resolver — the tab's
                predecessor once gated them differently, which left a clickable
                trigger over an empty panel. */}
            {showMetricsTab && (
              <TabsContent
                value="metrics"
                className={cn(TAB_CONTENT_CLASS, 'p-6 h-full overflow-y-auto')}
              >
                <MetricsTab />
              </TabsContent>
            )}

            <TabsContent value="runs" className={cn(TAB_CONTENT_CLASS, 'p-6')}>
              <AgentFlowRunsTab />
            </TabsContent>

            {showSimulationTab && (
              <TabsContent value="simulation" forceMount className={TAB_CONTENT_CLASS}>
                <SimulationTab />
              </TabsContent>
            )}

            {showEventCallbacksTab && (
              <TabsContent
                value="event-callbacks"
                className={cn(TAB_CONTENT_CLASS, 'p-6')}
                forceMount
              >
                <EventCallbacksTab />
              </TabsContent>
            )}

            {showSettingsTab && (
              <TabsContent value="settings" className={cn(TAB_CONTENT_CLASS, 'p-6')} forceMount>
                <AgentSettingsTab />
              </TabsContent>
            )}

            {showAgentResourceTabs && (
              <TabsContent value="knowledge-base" className={cn(TAB_CONTENT_CLASS, 'p-6')}>
                <AgentKnowledgeBaseTab />
              </TabsContent>
            )}

            {showAgentResourceTabs && (
              <TabsContent
                value="secrets"
                className={cn(TAB_CONTENT_CLASS, 'p-6 data-[state=active]:flex flex-col')}
              >
                <AgentSecretsTab />
              </TabsContent>
            )}

            {showAgentResourceTabs && (
              <TabsContent
                value="external-tools"
                className={cn(TAB_CONTENT_CLASS, 'p-6 data-[state=active]:flex flex-col')}
              >
                <IntegrationsMigrationBanner />
                <ExternalApps />
              </TabsContent>
            )}

            {showVoiceConfigTab && (
              <TabsContent value="voice-config" className={cn(TAB_CONTENT_CLASS, 'p-6')} forceMount>
                <VoiceConfigTab />
              </TabsContent>
            )}
          </div>
          <VibeAgentSidebar />
        </div>
      </Tabs>
    </div>
  )
}

const AgentBuilder = () => (
  <ReactFlowProvider>
    <AgentBuilderContent />
  </ReactFlowProvider>
)

export default AgentBuilder
